import { useEffect, useMemo, type ReactNode } from 'react';
import { createRoutesFromElements, useLocation, useNavigationType } from 'react-router-dom';
import { useAccount } from '../account/useAccount';
import { useAuth } from '../auth/useAuth';
import { pageViewNavigation, pageViewRoute } from './page-view.helpers';
import { hasRecordedInitialView, markInitialViewRecorded } from './ui-usage.runtime';
import { useTrackOnChange } from './ui-usage.hooks';

/**
 * Records one `ui-usage.page.viewed` per pathname change, using the route template that
 * matched. Renders nothing.
 *
 * Recording starts once the active account has loaded. Whether an event can be delivered is the
 * channel's decision (session token, restriction), so unmounting this component loses nothing.
 *
 * @param routes the `<Route>` elements of the table this recorder sits over, the same list the
 *   router renders (`authenticatedRoutes` or `wizardRoutes`).
 */
export function UiUsageRecorder({ routes }: { routes: ReactNode }) {
  const { accountId } = useAuth();
  const { account } = useAccount();
  const location = useLocation();
  const navigationType = useNavigationType();
  const routeObjects = useMemo(() => createRoutesFromElements(routes), [routes]);
  const activeAccountId = accountId !== null && account?.id === accountId ? accountId : null;
  const route = activeAccountId === null ? null : pageViewRoute(routeObjects, location.pathname);

  useTrackOnChange(
    'ui-usage.page.viewed',
    activeAccountId === null || route === null
      ? null
      : {
          route,
          navigation: pageViewNavigation(!hasRecordedInitialView(activeAccountId), navigationType),
        },
    `${activeAccountId}:${location.pathname}`,
  );

  // Declared after the hook so its effect has already read `initial` for this view.
  useEffect(() => {
    if (activeAccountId !== null && route !== null) markInitialViewRecorded(activeAccountId);
  }, [activeAccountId, route]);

  return null;
}
